import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import styled, { useTheme } from 'styled-components';

import type { ChartSegment } from '../../../utils/socChartUtils';
import Compress from '../../Icon/Compress';

import SocChart from './SocChart';
import type { NowToFutureLink } from './socChartDerivations';
import SocChartLegend from './SocChartLegend';
import SocChartToggles, { type TariffRateView } from './SocChartToggles';
import type { ShadingBlocks } from './useTariffShadingBlocks';

type ChartDataPoint = {
  x: string;
  y: number;
  _ts?: number;
  command?: string;
  startTime?: string;
  _previousSoc?: number;
  [key: string]: unknown;
};

export interface SocChartModalProps {
  show: boolean;
  onHide: () => void;
  chartTitle: string;
  chartAvailable: boolean;
  loopIsDisabled: boolean;
  importExportRatesDiffer: boolean;
  showBatteryActions: boolean;
  onToggleBatteryActions: () => void;
  showSolarGeneration: boolean;
  onToggleSolarGeneration: () => void;
  showTariffRates: boolean;
  onToggleTariffRates: () => void;
  tariffRateView: TariffRateView;
  onTariffRateViewChange: (view: TariffRateView) => void;
  importHasNoShading: boolean;
  exportHasNoShading: boolean;
  behaviorChartData: ChartDataPoint[] | null | undefined;
  historicChartData: ChartDataPoint[] | null | undefined;
  historicChartSegments: ChartSegment[];
  futureChartSegments: ChartSegment[] | null | undefined;
  nowToFutureLink: NowToFutureLink | null;
  safeActiveTariffShadingBlocks: ShadingBlocks;
  solarLineData: Array<{ x: string; y: number; _series: string; _solarKwh: number }>;
  solarLineDataLeft: Array<{ x: string; y: number; _series: string; _solarKwh: number }>;
  solarMaxKwh: number;
  tooltipBlacklist: string[];
  intersectionX: string | null | undefined;
  intersectionY: number;
  batteryCapacityKwh: number | undefined;
  placeholderXAxis: string[];
  nowOverrideSecs?: number;
}

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: min(1300px, calc(100vw - 40px));
    height: calc(100vh - 40px);
    margin: 20px auto;
  }
  .modal-content {
    border-radius: 20px;
    border: none;
    box-shadow: 0px 6px 24px rgba(0, 0, 0, 0.12);
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 30px 30px 16px;
`;

const ModalDivider = styled.hr`
  margin: 0 30px;
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.light};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.black};
`;

const MinimiseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ModalBody = styled.div`
  padding: 16px 30px 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
`;

const ChartFlexArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const SocChartModal: React.FC<SocChartModalProps> = ({
  show,
  onHide,
  chartTitle,
  chartAvailable,
  loopIsDisabled,
  importExportRatesDiffer,
  showBatteryActions,
  onToggleBatteryActions,
  showSolarGeneration,
  onToggleSolarGeneration,
  showTariffRates,
  onToggleTariffRates,
  tariffRateView,
  onTariffRateViewChange,
  importHasNoShading,
  exportHasNoShading,
  behaviorChartData,
  historicChartData,
  historicChartSegments,
  futureChartSegments,
  nowToFutureLink,
  safeActiveTariffShadingBlocks,
  solarLineData,
  solarLineDataLeft,
  solarMaxKwh,
  tooltipBlacklist,
  intersectionX,
  intersectionY,
  batteryCapacityKwh,
  placeholderXAxis,
  nowOverrideSecs,
}) => {
  const theme = useTheme();
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(480);
  const [chartWidth, setChartWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!show) return;
    const el = chartAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (height > 0) setChartHeight(Math.floor(height));
      if (width > 0) setChartWidth(Math.floor(width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [show]);

  return (
    <StyledModal show={show} onHide={onHide} centered>
      <ModalHeader>
        <ModalTitle>{chartTitle}</ModalTitle>
        <MinimiseButton onClick={onHide} aria-label="Close chart">
          <Compress colour={theme.colors.grey} width={22} height={22} />
        </MinimiseButton>
      </ModalHeader>
      <ModalDivider />
      <ModalBody>
        {chartAvailable && !loopIsDisabled && (
          <SocChartToggles
            showBatteryActions={showBatteryActions}
            onToggleBatteryActions={onToggleBatteryActions}
            showSolarGeneration={showSolarGeneration}
            onToggleSolarGeneration={onToggleSolarGeneration}
            showTariffRates={showTariffRates}
            onToggleTariffRates={onToggleTariffRates}
            importExportRatesDiffer={importExportRatesDiffer}
            tariffRateView={tariffRateView}
            onTariffRateViewChange={onTariffRateViewChange}
            importHasNoShading={importHasNoShading}
            exportHasNoShading={exportHasNoShading}
            inline
          />
        )}
        <ChartFlexArea ref={chartAreaRef}>
          <SocChart
            chartAvailable={chartAvailable}
            loopIsDisabled={loopIsDisabled}
            showBatteryActions={showBatteryActions}
            showSolarGeneration={showSolarGeneration}
            showTariffRates={showTariffRates}
            tariffRateView={tariffRateView}
            behaviorChartData={behaviorChartData}
            historicChartData={historicChartData}
            historicChartSegments={historicChartSegments}
            futureChartSegments={futureChartSegments}
            nowToFutureLink={nowToFutureLink}
            safeActiveTariffShadingBlocks={safeActiveTariffShadingBlocks}
            solarLineData={solarLineData}
            solarLineDataLeft={solarLineDataLeft}
            solarMaxKwh={solarMaxKwh}
            tooltipBlacklist={tooltipBlacklist}
            intersectionX={intersectionX}
            intersectionY={intersectionY}
            batteryCapacityKwh={batteryCapacityKwh}
            placeholderXAxis={placeholderXAxis}
            nowOverrideSecs={nowOverrideSecs}
            height={chartHeight}
            width={chartWidth}
          />
        </ChartFlexArea>
        {chartAvailable && (
          <SocChartLegend
            showBatteryActions={showBatteryActions}
            showTariffRates={showTariffRates}
            showSolarGeneration={showSolarGeneration}
            tariffRateView={tariffRateView}
          />
        )}
      </ModalBody>
    </StyledModal>
  );
};

export default SocChartModal;
